import React from "react";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { getHelpCenter } from "../../utils/urls";
import { Phone, Mail, MessageCircle, HelpCircle, ChevronDown } from "lucide-react";
import { useState } from "react";

function ContactCard({ icon: Icon, label, value, href }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="flex items-center gap-4 bg-white border border-[#E2E8F0] rounded-xl px-5 py-4 hover:border-[#0184FF] hover:shadow-[0_2px_8px_rgba(1,132,255,0.12)] transition-all duration-200 group"
    >
      <div className="w-11 h-11 rounded-xl bg-[#EAF4FF] flex items-center justify-center flex-shrink-0 group-hover:bg-[#0184FF] transition-colors duration-200">
        <Icon size={20} className="text-[#0184FF] group-hover:text-white transition-colors duration-200" />
      </div>
      <div>
        <p className="text-[0.75rem] text-[#64748B] leading-tight">{label}</p>
        <p className="text-[0.9375rem] font-semibold text-[#0F172A] leading-tight mt-0.5">{value}</p>
      </div>
    </a>
  );
}

function FaqItem({ question, answer }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-[#E2E8F0] rounded-xl overflow-hidden bg-white">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-[#F8FAFC] transition-colors"
      >
        <span className="text-[0.9375rem] font-semibold text-[#0F172A] pr-4">{question}</span>
        <ChevronDown
          size={18}
          className={`text-[#64748B] flex-shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        />
      </button>
      <div
        className="overflow-hidden transition-all duration-300"
        style={{ maxHeight: open ? "500px" : "0px" }}
      >
        <p className="px-5 pb-4 text-[0.875rem] text-[#475569] leading-relaxed border-t border-[#E2E8F0] pt-3">
          {answer}
        </p>
      </div>
    </div>
  );
}

const DEFAULT_FAQS = [
  { q: "How do I add a new lead?", a: "Go to the Leads tab and click 'Add New Lead'. Follow the 4-step form: enter lead details, select a category, choose a product, and review before submitting." },
  { q: "How is my commission calculated?", a: "Commission is calculated as (Lead Amount × Commission Percentage) / 100. The percentage is set by your product manager and shown in the product details." },
  { q: "Why can't I access some courses?", a: "Some courses require your advisor account to be verified (Approved status). Complete your onboarding to unlock all course content." },
  { q: "When will I receive my invoice?", a: "Invoices are generated automatically at the end of each billing cycle. You can filter by month and year in the Invoices tab." },
  { q: "How do I upload a certificate?", a: "Visit the Certificates section from the drawer menu. Enter a name for your certificate, then drag & drop or click to upload a PDF or image file." },
];

const HelpCenterPage = () => {
  const { data, isLoading } = useQuery({
    queryKey: ["help-center"],
    queryFn: async () => {
      const res = await axios.get(getHelpCenter);
      return res.data?.data;
    },
    retry: 1,
  });

  const contactNumber = data?.contact_number || data?.phone;
  const contactEmail = data?.email || data?.contact_email;
  const whatsapp = data?.whatsapp || data?.whatsapp_number || contactNumber;

  return (
    <div className="page-container flex-1">
      <div className="flex flex-col gap-8 max-w-3xl">
        <div className="pb-4 border-b border-[#E2EBF3]">
          <p className="text-[1.5rem] font-[600] text-[#0184FF]">Help Center</p>
          <p className="text-[0.875rem] text-[#64748B] mt-0.5">Get support from the AtmanirbharFins team</p>
        </div>

        {/* Contact cards */}
        <div>
          <p className="text-[0.9375rem] font-semibold text-[#0F172A] mb-3">Contact Us</p>
          {isLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[1, 2].map((i) => (
                <div key={i} className="h-20 bg-[#F1F5F9] rounded-xl animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {contactNumber && (
                <ContactCard
                  icon={Phone}
                  label="Call Us"
                  value={contactNumber}
                  href={`tel:${contactNumber}`}
                />
              )}
              {contactEmail && (
                <ContactCard
                  icon={Mail}
                  label="Email Us"
                  value={contactEmail}
                  href={`mailto:${contactEmail}`}
                />
              )}
              {whatsapp && (
                <ContactCard
                  icon={MessageCircle}
                  label="WhatsApp"
                  value={whatsapp}
                  href={`https://wa.me/${whatsapp?.replace(/\D/g, "")}`}
                />
              )}
              {!contactNumber && !contactEmail && !whatsapp && (
                <div className="col-span-2 py-8 text-center text-[#64748B] text-[0.875rem]">
                  Contact information not available. Please check back later.
                </div>
              )}
            </div>
          )}
        </div>

        {/* FAQ */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <HelpCircle size={18} className="text-[#0184FF]" />
            <p className="text-[0.9375rem] font-semibold text-[#0F172A]">Frequently Asked Questions</p>
          </div>
          <div className="flex flex-col gap-2">
            {DEFAULT_FAQS.map((faq, i) => (
              <FaqItem key={i} question={faq.q} answer={faq.a} />
            ))}
          </div>
        </div>

        {/* Version */}
        <div className="text-center py-4">
          <p className="text-[0.75rem] text-[#94A3B8]">AtmanirbharFins Advisor Portal · Web v1.0</p>
        </div>
      </div>
    </div>
  );
};

export default HelpCenterPage;
